import { Badge } from "../ui/badge"
import { Button } from "../ui/button"
import { motion } from "framer-motion"
import {
  ArrowRight,
  Star,
  TrendingUp,
  Clock,
  Building2,
  CheckCircle2,
  ShieldCheck,
  Zap
} from "lucide-react"

const categories = [
  { name: "Electrician", image: "https://images.unsplash.com/photo-1621905252507-b35492cc74b4?w=500&q=80", providers: 120 },
  { name: "Plumbing", image: "https://images.unsplash.com/photo-1585704032915-c3400ca199e7?w=500&q=80", providers: 85 },
  { name: "Cleaning", image: "https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=500&q=80", providers: 210 },
  { name: "AC Repair", image: "https://images.unsplash.com/photo-1620619932029-7c1bc1694f29?w=500&q=80", providers: 95 },
]

const providers = [
  { name: "John Smith", role: "Master Electrician", jobs: 98, response: "3 mins", image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&q=80" },
  { name: "Sarah Ahmed", role: "Expert Plumber", jobs: 145, response: "5 mins", image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&q=80" },
  { name: "Ali Raza", role: "HVAC Technician", jobs: 76, response: "2 mins", image: "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=400&q=80" },
]

export default function ServicesSection() {
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }
  }

  return (
    <div className="flex flex-col">
      {/* Categories Section */}
      <section className="bg-[#F8FAFC] pt-12 pb-24">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-4">
            <div>
              <div className="flex items-center gap-2 mb-3 text-brand-primary font-semibold uppercase tracking-wider text-sm">
                <Zap className="w-5 h-5" />
                <span>Quick Browse</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-brand-text tracking-tight">Popular Categories</h2>
            </div>
            <Button variant="link" className="hidden md:flex text-brand-primary hover:text-blue-800 font-semibold p-0 h-auto text-lg">
              View All <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>

          <motion.div 
            variants={containerVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {categories.map((category) => (
              <motion.div key={category.name} variants={itemVariants} className="group relative rounded-[24px] overflow-hidden cursor-pointer shadow-soft hover:shadow-premium transition-all duration-500 hover:-translate-y-2 bg-white">
                <div className="absolute inset-0 bg-gradient-to-t from-brand-text/90 via-brand-text/20 to-transparent z-10 transition-opacity group-hover:opacity-90"></div>
                
                {/* Gradient border effect via pseudo element or wrapper - simplified for React inline */}
                <div className="absolute inset-0 bg-gradient-to-br from-brand-primary to-brand-secondary opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10 blur-xl"></div>

                <img src={category.image} alt={category.name} className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute bottom-0 left-0 w-full p-6 z-20">
                  <h3 className="text-2xl font-bold text-white mb-2">{category.name}</h3>
                  <div className="flex items-center gap-2 text-white/80">
                    <CheckCircle2 className="w-4 h-4" />
                    <span className="text-sm font-medium">{category.providers} Verified Providers</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Featured Providers Section */}
      <section className="bg-white py-24">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-4">
            <div>
              <div className="flex items-center gap-2 mb-3 text-brand-accent font-semibold uppercase tracking-wider text-sm">
                <Star className="w-5 h-5 fill-current" />
                <span>Top Rated</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-brand-text tracking-tight">Featured Providers</h2>
            </div>
          </div>

          <motion.div 
            variants={containerVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-100px" }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {providers.map((provider) => (
              <motion.div key={provider.name} variants={itemVariants} className="bg-white rounded-[24px] p-6 shadow-soft hover:shadow-premium border border-slate-100 transition-all duration-500 hover:-translate-y-3 group relative">
                <div className="absolute top-6 right-6 bg-brand-success/10 text-brand-success px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                  <ShieldCheck className="w-4 h-4" /> Verified
                </div>
                
                <div className="flex items-center gap-5 mb-6">
                  <img src={provider.image} alt={provider.name} className="w-20 h-20 rounded-full object-cover border-4 border-slate-50 shadow-sm" />
                  <div>
                    <h3 className="text-xl font-bold text-brand-text group-hover:text-brand-primary transition-colors">{provider.name}</h3>
                    <p className="text-brand-muted text-sm font-medium">{provider.role}</p>
                    <div className="flex text-yellow-400 mt-1">
                      {[1, 2, 3, 4, 5].map((i) => <Star key={i} className="w-4 h-4 fill-current" />)}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="bg-slate-50 rounded-[16px] p-4 border border-slate-100">
                    <p className="text-xs text-brand-muted uppercase tracking-wider font-semibold mb-1">Completed</p>
                    <p className="text-lg font-bold text-brand-text">{provider.jobs} Jobs</p>
                  </div>
                  <div className="bg-slate-50 rounded-[16px] p-4 border border-slate-100">
                    <p className="text-xs text-brand-muted uppercase tracking-wider font-semibold mb-1">Response</p>
                    <p className="text-lg font-bold text-brand-text">{provider.response}</p>
                  </div>
                </div>

                <button className="w-full h-[52px] bg-white border-2 border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white rounded-[14px] font-semibold transition-all shadow-sm">
                  Book Now
                </button>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Featured Tenders Section */}
      <section className="bg-[#F8FAFC] py-24 border-y border-slate-100">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-4">
            <div>
              <div className="flex items-center gap-2 mb-3 text-brand-primary font-semibold uppercase tracking-wider text-sm">
                <TrendingUp className="w-5 h-5" />
                <span>Active Projects</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-brand-text tracking-tight">Featured Tenders</h2>
            </div>
            <Button variant="outline" className="hidden md:flex border-slate-200 hover:border-brand-primary hover:text-brand-primary transition-colors rounded-full px-8 h-[52px]">
              Browse Tenders
            </Button>
          </div>

          <motion.div 
            variants={containerVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {[1, 2, 3].map((item) => (
              <motion.div key={item} variants={itemVariants} className="bg-white rounded-[24px] overflow-hidden shadow-soft border border-slate-100 hover:shadow-premium transition-all duration-500 hover:-translate-y-2 flex flex-col h-full group">
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={`https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=800&q=80`}
                    alt="Tender"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <Badge className="absolute top-4 right-4 bg-brand-text text-white border-none shadow-md backdrop-blur-md">
                    Open to Bid
                  </Badge>
                </div>
                <div className="p-8 flex flex-col flex-grow">
                  <span className="text-xs font-bold text-brand-primary tracking-wider uppercase mb-2">Construction</span>
                  <h3 className="text-2xl font-bold text-brand-text mb-4 group-hover:text-brand-primary transition-colors">Commercial Plaza Build</h3>
                  <p className="text-brand-muted line-clamp-2 mb-8">Complete construction package including architectural design, raw materials, and labor management for a modern plaza in Lahore.</p>
                  
                  <div className="grid grid-cols-2 gap-4 mb-8 mt-auto">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center border border-slate-100">
                        <Clock className="w-4 h-4 text-brand-muted" />
                      </div>
                      <div>
                        <p className="text-[10px] text-brand-muted uppercase font-bold tracking-wider">Deadline</p>
                        <p className="text-sm font-semibold text-brand-text">15 Aug, 2026</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center border border-slate-100">
                        <Building2 className="w-4 h-4 text-brand-muted" />
                      </div>
                      <div>
                        <p className="text-[10px] text-brand-muted uppercase font-bold tracking-wider">Value</p>
                        <p className="text-sm font-semibold text-brand-text">Rs 50M+</p>
                      </div>
                    </div>
                  </div>
                  <button className="w-full h-[52px] bg-brand-text hover:bg-brand-primary text-white transition-colors rounded-[14px] shadow-sm font-semibold">
                    View Details
                  </button>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  )
}
